"use client";

import Link from "next/link";
import { Clock, BarChart3 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import {
  api,
  type MarketListItem,
  type UserSearchHit,
} from "@/lib/api";
import { usePickLocale } from "@/lib/usePickLocale";

/**
 * 2026-04-30 — global cmd-K (mac) / ctrl-K (win/linux) search
 * palette. Opens on either shortcut from anywhere in the app and
 * shows top 5 market hits + top 5 user hits for a debounced query.
 *
 * Not a replacement for /search — the dedicated page is for
 * deeper browsing. The palette is the keyboard-first power-user
 * surface (Linear / GitHub / Stocktwits all ship one).
 *
 * Behaviour:
 *   - cmd-K (or ctrl-K) anywhere → open
 *   - Esc → close
 *   - Backdrop click → close
 *   - Enter on a highlighted row → navigate
 *   - "Search markets…" placeholder; submit (Enter without highlight)
 *     navigates to /search?q=… for the full experience
 */

type Highlight =
  | { kind: "market"; index: number }
  | { kind: "user"; index: number };

// Recent-searches localStorage key + max retained.
const RECENT_KEY = "granbay.recentSearches";
const RECENT_MAX = 5;

function loadRecent(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((v): v is string => typeof v === "string" && v.length > 0)
      .slice(0, RECENT_MAX);
  } catch {
    return [];
  }
}

function pushRecent(q: string) {
  if (typeof window === "undefined") return;
  const trimmed = q.trim();
  if (trimmed.length === 0) return;
  try {
    const prev = loadRecent();
    // De-dupe (case-insensitive); most recent first.
    const next = [
      trimmed,
      ...prev.filter((p) => p.toLowerCase() !== trimmed.toLowerCase()),
    ].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // ignore — recents are decorative
  }
}

export function CmdKPalette() {
  const { t } = useTranslation();
  const pick = usePickLocale();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [markets, setMarkets] = useState<MarketListItem[]>([]);
  const [users, setUsers] = useState<UserSearchHit[]>([]);
  const [recents, setRecents] = useState<string[]>([]);
  const [highlight, setHighlight] = useState<Highlight | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Global keyboard shortcut + custom event trigger. Listens at
  // window level so cmd-K works even when focus is in another input
  // (Stocktwits + Linear behave this way). The "granbay.cmdk.open"
  // CustomEvent is the mobile-affordance hook — a button on the
  // SiteHeader can dispatch it to open the palette without a
  // keyboard.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isMac =
        typeof navigator !== "undefined" &&
        /Mac|iPhone|iPad/.test(navigator.platform);
      const trigger = (isMac ? e.metaKey : e.ctrlKey) && e.key === "k";
      if (trigger) {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    const onOpenEvent = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("granbay.cmdk.open", onOpenEvent);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("granbay.cmdk.open", onOpenEvent);
    };
  }, []);

  // Body-scroll lock + autofocus when opening. Also hydrate the
  // recent-searches list so it appears on the empty-input view.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    inputRef.current?.focus();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setRecents(loadRecent());
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Debounced fetch.
  useEffect(() => {
    if (!open) return;
    const q = input.trim();
    /* eslint-disable react-hooks/set-state-in-effect --
       Resetting both result sets when the input clears is the
       intended behaviour; the rule over-flags this debounced-fetch
       pattern. */
    if (q === "") {
      setMarkets([]);
      setUsers([]);
      return;
    }
    /* eslint-enable react-hooks/set-state-in-effect */
    let cancelled = false;
    const debounce = setTimeout(() => {
      void Promise.allSettled([
        api.listMarkets({ q, limit: 5 }),
        api.searchUsers(q, 5),
      ]).then(([m, u]) => {
        if (cancelled) return;
        if (m.status === "fulfilled") setMarkets(m.value.markets);
        if (u.status === "fulfilled") setUsers(u.value.users);
      });
    }, 150);
    return () => {
      cancelled = true;
      clearTimeout(debounce);
    };
  }, [input, open]);

  function close() {
    setOpen(false);
    setInput("");
    setMarkets([]);
    setUsers([]);
    setHighlight(null);
  }

  // Flat ordering for ↑/↓ navigation: users first (matches the
  // visual order on the page), markets second. Highlight indexes
  // walk this combined list.
  const flatRows = [
    ...users.map((u, i) => ({
      id: `u:${u.username}`,
      kind: "user" as const,
      index: i,
      href: `/u/${u.username}`,
    })),
    ...markets.map((m, i) => ({
      id: `m:${m.id}`,
      kind: "market" as const,
      index: i,
      href: `/markets/${m.slug}`,
    })),
  ];

  function moveHighlight(delta: 1 | -1) {
    if (flatRows.length === 0) return;
    const currentFlatIdx = flatRows.findIndex(
      (r) => highlight && r.kind === highlight.kind && r.index === highlight.index,
    );
    let next = currentFlatIdx + delta;
    if (currentFlatIdx === -1) {
      next = delta === 1 ? 0 : flatRows.length - 1;
    }
    // Wrap around so the keyboard navigation feels continuous.
    if (next < 0) next = flatRows.length - 1;
    if (next >= flatRows.length) next = 0;
    const target = flatRows[next];
    setHighlight({ kind: target.kind, index: target.index });
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      moveHighlight(1);
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      moveHighlight(-1);
      return;
    }
    if (e.key === "Enter") {
      // If a row is highlighted → navigate there. Otherwise fall
      // back to /search?q=… for the full results page.
      const currentFlatIdx = flatRows.findIndex(
        (r) =>
          highlight && r.kind === highlight.kind && r.index === highlight.index,
      );
      // Persist whatever the user actually searched for so the
      // recent-searches list reflects intent (the typed query),
      // not the destination they happened to click.
      if (input.trim()) pushRecent(input.trim());
      if (currentFlatIdx >= 0) {
        e.preventDefault();
        router.push(flatRows[currentFlatIdx].href);
        close();
      } else if (input.trim()) {
        router.push(`/search?q=${encodeURIComponent(input.trim())}`);
        close();
      }
    }
  }

  // Reset highlight whenever the result set changes — otherwise
  // a stale highlight on a removed row keeps Enter pointing at
  // nothing.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setHighlight(null);
  }, [users, markets]);

  if (!open) return null;
  if (typeof document === "undefined") return null;
  // SiteHeader's backdrop-blur-md creates a containing block for
  // `position: fixed` descendants — without a Portal this overlay
  // anchors to the header's box instead of the viewport. createPortal
  // escapes to <body>.
  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center pt-[15vh]"
      role="dialog"
      aria-modal="true"
      aria-label={t("cmdKPalette.ariaLabel")}
    >
      <button
        type="button"
        aria-label={t("cmdKPalette.closeAria")}
        onClick={close}
        className="absolute inset-0 cursor-default bg-black/60 backdrop-blur-sm"
      />
      <div className="relative w-[min(92vw,560px)] overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] shadow-[0_24px_64px_rgba(0,0,0,0.5)]">
        <input
          ref={inputRef}
          type="search"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t("cmdKPalette.placeholder")}
          className="w-full border-b border-[var(--color-border)] bg-transparent px-4 py-3 text-[15px] text-[var(--color-text)] outline-none"
        />

        <div className="max-h-[50vh] overflow-y-auto p-2">
          {markets.length === 0 &&
            users.length === 0 &&
            input.trim() === "" && (
              <>
                {recents.length > 0 ? (
                  <Section label={t("cmdKPalette.recentSearches")}>
                    {recents.map((q) => (
                      <li key={q}>
                        <button
                          type="button"
                          onClick={() => {
                            setInput(q);
                          }}
                          className="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left transition-colors hover:bg-[var(--color-surface)]"
                        >
                          <Clock
                            size={14}
                            strokeWidth={1.5}
                            className="text-[var(--color-text3)]"
                            aria-hidden="true"
                          />
                          <span className="text-[13px] text-[var(--color-text2)]">
                            {q}
                          </span>
                        </button>
                      </li>
                    ))}
                  </Section>
                ) : (
                  <p className="px-3 py-2 text-[12px] text-[var(--color-text3)]">
                    {t("cmdKPalette.emptyHint")}
                  </p>
                )}
              </>
            )}

          {users.length > 0 && (
            <Section label={t("cmdKPalette.users")}>
              {users.map((u, i) => (
                <Row
                  key={u.username}
                  highlighted={
                    highlight?.kind === "user" && highlight.index === i
                  }
                  onMouseEnter={() => setHighlight({ kind: "user", index: i })}
                  href={`/u/${u.username}`}
                  onNavigate={close}
                >
                  <span
                    className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white"
                    style={{
                      background:
                        "linear-gradient(135deg, #8957e5, #ec4899)",
                    }}
                    aria-hidden="true"
                  >
                    {u.username.charAt(0).toUpperCase()}
                  </span>
                  <span className="text-[13px] font-bold text-[var(--color-text)]">
                    {u.displayName || u.username}
                  </span>
                  {u.displayName && (
                    <span className="text-[11px] text-[var(--color-text3)]">
                      @{u.username}
                    </span>
                  )}
                </Row>
              ))}
            </Section>
          )}

          {markets.length > 0 && (
            <Section label={t("cmdKPalette.markets")}>
              {markets.map((m, i) => {
                const title =
                  typeof m.title === "string" ? m.title : pick(m.title);
                return (
                  <Row
                    key={m.id}
                    highlighted={
                      highlight?.kind === "market" && highlight.index === i
                    }
                    onMouseEnter={() =>
                      setHighlight({ kind: "market", index: i })
                    }
                    href={`/markets/${m.slug}`}
                    onNavigate={close}
                  >
                    <BarChart3
                      size={14}
                      strokeWidth={1.5}
                      className="text-[var(--color-text3)]"
                      aria-hidden="true"
                    />
                    <span className="line-clamp-1 text-[13px] text-[var(--color-text)]">
                      {title}
                    </span>
                    {m.yesPriceCents !== null && (
                      <span
                        className="ml-auto font-mono text-[11px] tabular-nums"
                        style={{
                          color:
                            m.yesPriceCents > 50
                              ? "var(--color-yes)"
                              : m.yesPriceCents < 50
                                ? "var(--color-no)"
                                : "var(--color-text3)",
                        }}
                      >
                        YES {m.yesPriceCents}¢
                      </span>
                    )}
                  </Row>
                );
              })}
            </Section>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-[var(--color-border)] px-3 py-2 text-[10px] text-[var(--color-text3)]">
          <span>{t("cmdKPalette.footerNav")}</span>
          <span className="font-mono">
            {typeof navigator !== "undefined" &&
            /Mac|iPhone|iPad/.test(navigator.platform)
              ? "⌘K"
              : "Ctrl+K"}
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Section({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-1.5">
      <div className="px-3 pb-1 pt-2 text-[9px] font-bold uppercase tracking-[0.6px] text-[var(--color-text3)]">
        {label}
      </div>
      <ul>{children}</ul>
    </div>
  );
}

function Row({
  href,
  highlighted,
  onMouseEnter,
  onNavigate,
  children,
}: {
  href: string;
  highlighted: boolean;
  onMouseEnter: () => void;
  onNavigate: () => void;
  children: React.ReactNode;
}) {
  return (
    <li>
      <Link
        href={href}
        onClick={onNavigate}
        onMouseEnter={onMouseEnter}
        className="flex items-center gap-2 rounded-md px-3 py-1.5 transition-colors"
        style={{
          background: highlighted ? "var(--color-surface)" : "transparent",
        }}
      >
        {children}
      </Link>
    </li>
  );
}
